import "@tento-ui/components/prosemirror";

import { Div } from "@tento-ui/component-builtin";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { Button } from "@tento-ui/components/button/button";
import { ProseMirrorEditor } from "@tento-ui/components/prosemirror";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  RenderPlugin,
  type StaticChildSpec,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { LonaIcons, TextIcon } from "@tento-ui/ui/icons";
import { Json, type JsonObject, type Option, RowId, RowNetworkKey } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { $$ } from "@tento-ui/fastdom";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import { deepFrozen } from "@tento-lona/sheets/internal-ui";

/** The inert-row plugin for a label row: editable text across the sheet. */
export class LabelInertPlugin
  implements RenderPlugin.Inert<HTMLDivElement, SheetLegend.DefaultRow>
{
  readonly role = "inert";
  readonly id: string;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(TextIcon);
  #labels = new Map<RowLocalId, LabelHeader>();

  readonly #patchRowAttributes: (
      rowId: RowId,
      attributes: JsonObject,
    ) => void;
  readonly #config: LabelInertPlugin.Config;
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    patchRowAttributes: (
      rowId: RowId,
      attributes: JsonObject,
    ) => void,
    config: LabelInertPlugin.Config = LabelInertPlugin.Config.DEFAULT,
  ) {
    this.#config = config;
    this.#patchRowAttributes = patchRowAttributes;
    this.id = config.id;
    this.legendFactory = config.showLegend
      ? LabelInertPlugin.DefaultLegendFactory
      : null;
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return this.#config.optionsInfoMessage;
  }

  makeCell(): HTMLDivElement {
    return Div.make();
  }

  bindCell(
    _$cell: HTMLDivElement,
    context: BindCellContext<SheetRow.Joined.Handle>,
  ): void {
    if (this.#config.staticChildrenOnly) return;
    this.#bindBanner(context.sheetRow, context.handle);
  }

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}

  staticChildren(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
  ): StaticChildSpec[] {
    if (this.#config.staticChildrenOnly && row.surface !== "footer") return [];
    const $label = this.#bindBanner(row, handle);
    return [{ id: this.id, $element: $label, fillWidth: true }];
  }

  onResize(
    _$cell: HTMLDivElement,
    _context: BindCellContext<SheetRow.Joined.Handle>,
  ): void {}

  loadData = null;

  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>>;

  static readonly DefaultLegendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        { defaultLabel: "Label" },
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };

  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  #bindBanner(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
  ): LabelHeader {
    const attrs = row.attributesAs(LabelInertPlugin.decodeAttributes) ?? LabelInertPlugin.decodeAttributes({});
    const editable =
      (attrs.editable ?? this.#config.defaultEditable) &&
      handle.state.sheet.editable;
    // Pattern A (6f) — pull labelText off the same attrs bag used below.
    const labelText = attrs.labelText ?? null;

    let $label = this.#labels.get(row.id);
    if (!$label) {
      $label = LabelHeader.make();
      this.#labels.set(row.id, $label);
    }

    $label.bind({
      rowId: RowNetworkKey.of(row.id),
      content: attrs.content ?? labelText ?? "",
      editable,
      hasBackground: attrs.hasBackground ?? this.#config.defaultHasBackground,
      textAlign: attrs.textAlign ?? this.#config.defaultTextAlign,
      ctaPlacement: attrs.ctaPlacement ?? "inline",
      ctas: attrs.ctas ?? this.#legacyCtas(attrs),
      severity: attrs.severity ?? null,
      severityIcon: attrs.severityIcon ?? null,
      dismissible: attrs.dismissible ?? false,
      dismissed: attrs.dismissed ?? false,
      onUpdate: (md) => {
        const currentAttrs = row.attributes() ?? {};
        this.#patchRowAttributes(RowId.fromLocalId(row.id), {
          ...currentAttrs,
          content: md,
          labelText: md,
        });
      },
      onDismiss: () => {
        const currentAttrs = row.attributes() ?? {};
        this.#patchRowAttributes(RowId.fromLocalId(row.id), {
          ...currentAttrs,
          dismissed: true,
        });
      },
      onCta: (detail) => {
        if (detail.action.type !== "named") return;
        this.#config.actions[detail.action.name]?.();
      },
    });

    const height = row.layoutSpec(handle.state.sheet.timescale).cellHeight;
    $label.bindHeight(height);
    return $label;
  }

  #legacyCtas(attrs: LabelInertPlugin.Attributes): LabelInertPlugin.Cta[] {
    if (!attrs.ctaLabel || !attrs.ctaAction) return [];
    return [
      {
        id: attrs.ctaAction,
        label: attrs.ctaLabel,
        style: "primary",
        action: {
          type: "named",
          name: attrs.ctaAction,
        },
      },
    ];
  }
}

/**
 * Types and values that go with `LabelInertPlugin`: `Severity`, `CtaAction`,
 * `Cta`, `Attributes`, `decodeAttributes`, `Actions`, and 1 more.
 */
export namespace LabelInertPlugin {
  export type Severity = "info" | "warning" | "critical";

  export type CtaAction =
    | {
        type: "named";
        name: string;
      }
    | {
        type: "link";
        href: string;
      };

  export type Cta = {
    id: string;
    label: string;
    style: Option<"primary" | "secondary" | "flat">;
    action: CtaAction;
  };

  export type Attributes = {
    labelText: Option<string>;
    content: Option<string>;
    editable: Option<boolean>;
    hasBackground: Option<boolean>;
    textAlign: Option<"left" | "center" | "right">;
    ctaPlacement: Option<"inline" | "below">;
    severity: Option<Severity>;
    severityIcon: Option<string>;
    dismissible: Option<boolean>;
    dismissed: Option<boolean>;
    ctas: Option<Cta[]>;
    ctaLabel: Option<string>;
    ctaAction: Option<string>;
  };

  const TEXT_ALIGNS: readonly ("left" | "center" | "right")[] = ["left", "center", "right"];
  const CTA_PLACEMENTS: readonly ("inline" | "below")[] = ["inline", "below"];
  const SEVERITIES: readonly Severity[] = ["info", "warning", "critical"];
  const CTA_STYLES: readonly ("primary" | "secondary" | "flat")[] = [
    "primary",
    "secondary",
    "flat",
  ];

  /** Read a label row's display config off its bag; malformed CTAs are dropped. */
  export function decodeAttributes(attributes: JsonObject): Attributes {
    const ctas = Json.array(attributes, "ctas");
    return {
      labelText: Json.string(attributes, "labelText"),
      content: Json.string(attributes, "content"),
      editable: Json.boolean(attributes, "editable"),
      hasBackground: Json.boolean(attributes, "hasBackground"),
      textAlign: Json.oneOf(attributes, "textAlign", TEXT_ALIGNS),
      ctaPlacement: Json.oneOf(attributes, "ctaPlacement", CTA_PLACEMENTS),
      severity: Json.oneOf(attributes, "severity", SEVERITIES),
      severityIcon: Json.string(attributes, "severityIcon"),
      dismissible: Json.boolean(attributes, "dismissible"),
      dismissed: Json.boolean(attributes, "dismissed"),
      ctas: ctas == null ? null : decodeCtas(ctas),
      ctaLabel: Json.string(attributes, "ctaLabel"),
      ctaAction: Json.string(attributes, "ctaAction"),
    };
  }

  function decodeCtas(list: readonly Json[]): Cta[] {
    const out: Cta[] = [];
    for (const item of list) {
      const obj = Json.object(item);
      const id = Json.string(obj, "id");
      const label = Json.string(obj, "label");
      const action = decodeCtaAction(Json.child(obj, "action"));
      if (id == null || label == null || action == null) continue;
      out.push({ id, label, style: Json.oneOf(obj, "style", CTA_STYLES), action });
    }
    return out;
  }

  function decodeCtaAction(obj: Option<JsonObject>): Option<CtaAction> {
    const type = Json.string(obj ?? null, "type");
    if (type === "named") {
      const name = Json.string(obj ?? null, "name");
      return name == null ? null : { type, name };
    }
    if (type === "link") {
      const href = Json.string(obj ?? null, "href");
      return href == null ? null : { type, href };
    }
    return null;
  }

  export type Actions = Record<string, () => void>;

  export type Config = {
    id: string;
    defaultEditable: boolean;
    defaultHasBackground: boolean;
    defaultTextAlign: "left" | "center" | "right";
    staticChildrenOnly: boolean;
    actions: Actions;
    optionsInfoMessage: Option<string>;
    showLegend: boolean;
  };

  export namespace Config {
    export const DEFAULT: Config = deepFrozen({
      id: "label",
      defaultEditable: true,
      defaultHasBackground: false,
      defaultTextAlign: "left",
      staticChildrenOnly: false,
      actions: {},
      optionsInfoMessage:
        "Display a simple label with customizable text and height. Perfect for section headers or visual separators.",
      showLegend: true,
    });
  }
}

/**
 * The `lona-label-header` element: a label row's text with its call to
 * action.
 */
@component({
  name: "lona-label-header",
})
export class LabelHeader extends LonaWebComponent {
  #$editor: ProseMirrorEditor = this.hydrate("editor");
  #$ctaRow: HTMLDivElement = this.$("cta-row");
  #$severityIcon: HTMLSpanElement = this.$("severity-icon");
  #$dismissRow: HTMLDivElement = this.$("dismiss-row");
  #currentMarkdown: Option<string> = null;
  #currentEditable: Option<boolean> = null;

  bindHeight(heightPx: number) {
    const desiredHeight = heightPx + "px";
    $$.measure(() => {
      if (this.style.height === desiredHeight) return;
      const height = this.offsetHeight;
      $$.mutate(() => {
        if (heightPx === height) return;
        this.style.height = desiredHeight;
      });
    });
  }

  onRecycle() {
    this.#currentMarkdown = null;
    this.#currentEditable = null;
    DomUtils.clearChildren(this.#$ctaRow);
    DomUtils.clearChildren(this.#$dismissRow);
    this.removeAttribute("severity");
    this.toggleAttribute("hidden", false);
  }

  bind(props: LabelHeader.Props): this {
    this.onpointerup = props.editable ? () => this.#$editor.focusEditor() : null;
    this.toggleAttribute("with-background", props.hasBackground);
    this.toggleAttribute("hidden", props.dismissed);
    this.toggleAttribute("editable", props.editable);
    this.setAttribute("cta-placement", props.ctaPlacement);
    this.style.textAlign = props.textAlign;
    if (props.severity) {
      this.setAttribute("severity", props.severity);
    } else {
      this.removeAttribute("severity");
    }

    const icon = props.severityIcon ?? defaultSeverityIcon(props.severity);
    this.#$severityIcon.textContent = icon ?? "";
    this.#$severityIcon.toggleAttribute("hidden", icon === null);

    const mdChanged = this.#currentMarkdown !== props.content;
    const editableChanged = this.#currentEditable !== props.editable;

    if (mdChanged || editableChanged || !this.#$editor.initialized) {
      this.#currentMarkdown = props.content;
      this.#currentEditable = props.editable;
      this.#$editor.bind({
        markdown: props.content ?? "",
        editable: props.editable,
        placeholder: props.editable ? "Enter label..." : "",
        onUpdate: (md) => {
          this.#currentMarkdown = md;
          props.onUpdate?.(md);
        },
      });
    }

    DomUtils.clearChildren(this.#$ctaRow);
    for (const cta of props.ctas) {
      const $cta = Button.makeWith(cta.label, {
        type: cta.style ?? "primary",
        onClick: () => {
          const detail: LabelHeader.CtaEventDetail = {
            rowId: props.rowId,
            ctaId: cta.id,
            action: cta.action,
          };
          this.dispatchEvent(
            new CustomEvent("banner-cta", {
              bubbles: true,
              composed: true,
              detail,
            }),
          );
          props.onCta?.(detail);
        },
      });
      $cta.toggleAttribute("thin", props.ctaPlacement === "inline");
      this.#$ctaRow.appendChild($cta);
    }
    this.#$ctaRow.toggleAttribute("hidden", props.ctas.length === 0);

    DomUtils.clearChildren(this.#$dismissRow);
    if (props.dismissible) {
      const $dismiss = Button.makeWith("Dismiss", {
        type: "flat",
        onClick: () => props.onDismiss?.(),
      });
      $dismiss.toggleAttribute("thin", true);
      this.#$dismissRow.appendChild($dismiss);
    }
    this.#$dismissRow.toggleAttribute("hidden", !props.dismissible);

    return this;
  }

  static $styles: Style.Compat[] = [
    Scrollbars.$hideScrollbars,
    css`
      :host {
        width: 100%;
        height: 100%;
      }

      #root {
        align-items: stretch;
        justify-content: center;
        height: 100%;
        margin-top: 2px;
        margin-inline: 1px;
        cursor: default;
        --scroll-bar-style: none;
        gap: 8px;
        padding: 4px 8px;
      }

      :host([editable]) #root {
        cursor: text;
      }

      :host([cta-placement="below"]) #root {
        flex-direction: column;
        align-items: center;
        gap: 4px;
        margin-inline: 5px;
        padding: 8px 12px;
        margin-bottom: 2px;
        height: calc(100% - 4px);
      }

      :host([hidden]) {
        display: none;
      }

      :host([with-background]) #root {
        background: var(--bg-surface-light);
        border-radius: 8px;
      }

      /* On the lighter with-background surface the neutral primary CTA blends in.
         Use a mid shade of the bg: black in light mode, --bg-elevated in dark
         mode (--bg #1e222c reads too black, --bg-elevated-2 #354158 too light;
         --bg-elevated #2a3342 sits between). Nested light-dark resolves
         --bg-elevated to its dark value. */
      :host([with-background]) #cta-row std-button {
        --bg-btn-pri: light-dark(black, var(--bg-elevated));
      }

      :host([severity="info"]) #root {
        background: var(--blue-dimmed);
      }

      :host([severity="warning"]) #root {
        background: var(--yellow-dimmed);
      }

      :host([severity="critical"]) #root {
        background: var(--red-dimmed);
      }

      #severity-icon {
        color: var(--text-sec);
        flex: 0 0 auto;
        padding-top: 8px;
      }

      #severity-icon[hidden] {
        display: none;
      }

      #editor {
        width: 100%;
        height: 100%;
        --editor-padding: 8px 12px;
        color: var(--text-pri);
        border-radius: 8px;
        flex: 1;
      }

      :host([cta-placement="below"]) #editor {
        height: auto;
        --editor-padding: 8px 16px;
        --font-size: var(--text-11px);
        flex: 0 1 auto;
      }

      #cta-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      :host([cta-placement="below"]) #cta-row {
        justify-content: center;
      }

      #cta-row[hidden] {
        display: none;
      }

      #dismiss-row[hidden] {
        display: none;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <span id="severity-icon"></span>
      <std-prosemirror id="editor"></std-prosemirror>
      <div id="cta-row"></div>
      <div id="dismiss-row"></div>
    </std-row>
  `;
}

function defaultSeverityIcon(
  severity: Option<LabelInertPlugin.Severity>,
): Option<string> {
  switch (severity) {
    case "info":
      return "i";
    case "warning":
      return "!";
    case "critical":
      return "!";
    case null:
    case undefined:
      return null;
  }
}

/** Types that go with `LabelHeader`: `CtaEventDetail`, `Props`. */
export namespace LabelHeader {
  export type CtaEventDetail = {
    rowId: RowNetworkKey;
    ctaId: string;
    action: LabelInertPlugin.CtaAction;
  };

  export type Props = {
    rowId: RowNetworkKey;
    content: string;
    editable: boolean;
    hasBackground: boolean;
    textAlign: "left" | "center" | "right";
    ctaPlacement: "inline" | "below";
    severity: Option<"info" | "warning" | "critical">;
    severityIcon: Option<string>;
    dismissible: boolean;
    dismissed: boolean;
    ctas: LabelInertPlugin.Cta[];
    onUpdate?: (markdown: string) => void;
    onDismiss?: () => void;
    onCta?: (detail: CtaEventDetail) => void;
  };
}
